import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquare, Plus, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchChats, fetchChatDetail, fetchMoreClosedChats, createChat, sendMessage, clearChats, clearSendError } from '../store/chatSlice'
import type { ChatImageBlock } from '../store/chatSlice'
import { login } from './UserMenu'
import ChatPanel from './ChatPanel'
import './ChatList.css'

const CHAT_POLL_INTERVAL = 5000

// Coding agents an editor can pick for a new chat's container. The `id` values
// must match CODING_AGENTS in netlify/functions/lib/config.ts.
const CODING_AGENTS = [
  { id: 'claude', label: 'Claude' },
  { id: 'codex', label: 'Codex' },
  { id: 'pi', label: 'Pi (Fireworks)' },
] as const

type CodingAgentId = (typeof CODING_AGENTS)[number]['id']

interface ChatListProps {
  appId: string
  /** Whether the app opens pull requests — enables the branch-name option. */
  prEnabled?: boolean
}

function ChatList({ appId, prEnabled = false }: ChatListProps) {
  const dispatch = useAppDispatch()
  const { items, loading, creating, sending, sendErrors, closedTotal, closedLoaded, loadingMore } = useAppSelector((state) => state.chats)
  const user = useAppSelector((state) => state.auth.user)
  const activeChatsRef = useRef<string[]>([])

  useEffect(() => {
    dispatch(fetchChats(appId))
    return () => { dispatch(clearChats()) }
  }, [dispatch, appId])

  // Fetch messages for active chats that haven't been loaded yet. The dependency
  // is a stable key of exactly the chat ids still needing a fetch — not `items`
  // itself — so unrelated mutations (a poll refresh, another chat's detail
  // resolving) don't re-trigger the whole batch. The per-chat in-flight guard in
  // fetchChatDetail is the backstop that keeps this to one request per chat.
  const activeUnloadedKey = useMemo(
    () => items.filter((c) => c.status === 'active' && !c.messages).map((c) => c.id).join(','),
    [items],
  )
  useEffect(() => {
    if (!activeUnloadedKey) return
    for (const chatId of activeUnloadedKey.split(',')) {
      dispatch(fetchChatDetail({ appId, chatId }))
    }
  }, [dispatch, appId, activeUnloadedKey])

  // Load messages for a chat on demand (e.g. when expanding a closed chat)
  const handleExpand = (chatId: string) => {
    const chat = items.find((c) => c.id === chatId)
    if (chat && !chat.messages) {
      dispatch(fetchChatDetail({ appId, chatId }))
    }
  }

  // Poll for new messages on active chats (only when page is visible)
  useEffect(() => {
    const activeIds = items.filter((c) => c.status === 'active').map((c) => c.id)
    activeChatsRef.current = activeIds
    if (activeIds.length === 0) return

    const poll = () => {
      if (document.visibilityState !== 'visible') return
      dispatch(fetchChats(appId))
      for (const chatId of activeChatsRef.current) {
        dispatch(fetchChatDetail({ appId, chatId }))
      }
    }

    const interval = setInterval(poll, CHAT_POLL_INTERVAL)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [dispatch, appId, items.filter((c) => c.status === 'active').map((c) => c.id).join(',')])

  // New Chat options (revealed via the caret menu). The branch a PR-mode chat
  // works on defaults to an auto-generated one server-side, but the editor can
  // override it here; the coding agent defaults to Claude.
  const [optionsOpen, setOptionsOpen] = useState(false)
  const [branch, setBranch] = useState('')
  const [agent, setAgent] = useState<CodingAgentId>('claude')
  // Viewport-anchored position for the portaled options menu. The menu is
  // rendered into document.body (not inside .chat-list) so the card's
  // `overflow: hidden` cannot clip it — a plain absolutely-positioned dropdown
  // was being cut off, making the controls unusable.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Recompute the viewport-anchored position from the caret's bounding rect.
  const positionMenu = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect()
    if (rect) {
      setMenuPos({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) })
    }
  }, [])

  const openOptions = () => {
    positionMenu()
    setOptionsOpen(true)
  }

  const handleCreate = () => {
    if (!user) {
      login(window.location.pathname)
      return
    }
    setOptionsOpen(false)
    const trimmedBranch = branch.trim()
    dispatch(createChat({
      appId,
      // Branch only applies in PR mode; omit it otherwise so the server keeps
      // its default behavior.
      ...(prEnabled && trimmedBranch ? { branch: trimmedBranch } : {}),
      ...(agent !== 'claude' ? { agent } : {}),
    }))
  }

  // Close the options menu on outside click or Escape, and on scroll/resize
  // (the menu is viewport-anchored, so it would otherwise drift from its button).
  useEffect(() => {
    if (!optionsOpen) return
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setOptionsOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOptionsOpen(false)
    }
    const onReflow = () => {
      // On mobile, focusing the branch-name input opens the soft keyboard, which
      // shrinks the visual viewport and fires a window resize (and sometimes a
      // scroll). That must NOT dismiss the menu — the editor is mid-interaction.
      // When focus is inside the menu, reposition it against its anchor instead
      // of closing; a genuine scroll/resize with focus elsewhere still closes it.
      if (menuRef.current?.contains(document.activeElement)) {
        positionMenu()
        return
      }
      setOptionsOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', onReflow)
    window.addEventListener('scroll', onReflow, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onReflow)
      window.removeEventListener('scroll', onReflow, true)
    }
  }, [optionsOpen, positionMenu])

  const [interrupting, setInterrupting] = useState<Record<string, boolean>>({})
  const [stopping, setStopping] = useState<Record<string, boolean>>({})

  const handleInterrupt = useCallback(async (chatId: string) => {
    const chat = items.find((c) => c.id === chatId)
    if (!chat?.container_id) return
    setInterrupting((prev) => ({ ...prev, [chatId]: true }))
    try {
      const resp = await fetch('/api/interrupt-container', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ containerId: chat.container_id }),
      })
      if (resp.ok) {
        dispatch(fetchChats(appId))
        dispatch(fetchChatDetail({ appId, chatId }))
      }
    } catch {
      // best effort
    } finally {
      setInterrupting((prev) => ({ ...prev, [chatId]: false }))
    }
  }, [items, appId, dispatch])

  const handleStop = useCallback(async (chatId: string) => {
    const chat = items.find((c) => c.id === chatId)
    if (!chat?.container_id) return
    setStopping((prev) => ({ ...prev, [chatId]: true }))
    try {
      const resp = await fetch('/api/stop-container', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ containerId: chat.container_id }),
      })
      if (resp.ok) {
        dispatch(fetchChats(appId))
      }
    } catch {
      // best effort
    } finally {
      setStopping((prev) => ({ ...prev, [chatId]: false }))
    }
  }, [items, appId, dispatch])

  const handleSend = (chatId: string, content: string, images?: ChatImageBlock[]) => {
    dispatch(sendMessage({ appId, chatId, content, images }))
  }

  const [closedExpanded, setClosedExpanded] = useState(false)

  const activeChats = useMemo(() => items.filter((c) => c.status === 'active'), [items])
  const closedChats = useMemo(() => items.filter((c) => c.status !== 'active'), [items])

  return (
    <section className="chat-list" data-testid="chat-list" aria-labelledby="chat-list-heading">
      <div className="chat-list__header">
        <h2 className="chat-list__title" id="chat-list-heading">
          <MessageSquare size={20} aria-hidden="true" />
          Chats
        </h2>
        <div className="chat-list__new" ref={anchorRef}>
          <div className="chat-list__new-split">
            <button
              className="chat-list__new-btn"
              onClick={handleCreate}
              disabled={creating}
              data-testid="new-chat-btn"
            >
              {creating ? <Loader2 size={16} className="chat-list__spinner" aria-hidden="true" /> : <Plus size={16} aria-hidden="true" />}
              {creating ? 'Starting...' : 'New Chat'}
            </button>
            <button
              className="chat-list__new-caret"
              onClick={() => (optionsOpen ? setOptionsOpen(false) : openOptions())}
              disabled={creating}
              aria-haspopup="menu"
              aria-expanded={optionsOpen}
              aria-label="New chat options"
              data-testid="new-chat-options-toggle"
            >
              <ChevronDown size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
        {optionsOpen && menuPos && createPortal(
          <div
            className="chat-list__new-menu"
            role="menu"
            data-testid="new-chat-options-menu"
            ref={menuRef}
            style={{ top: menuPos.top, right: menuPos.right }}
          >
            <div className="chat-list__new-field">
              <label className="chat-list__new-label" htmlFor="new-chat-agent">
                Coding agent
              </label>
              <select
                id="new-chat-agent"
                className="chat-list__new-select"
                value={agent}
                onChange={(e) => setAgent(e.target.value as CodingAgentId)}
                data-testid="new-chat-agent-select"
              >
                {CODING_AGENTS.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </div>
            {prEnabled && (
              <div className="chat-list__new-field">
                <label className="chat-list__new-label" htmlFor="new-chat-branch">
                  Branch name
                </label>
                <input
                  id="new-chat-branch"
                  className="chat-list__new-input"
                  type="text"
                  placeholder="Auto-generated if blank"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  data-testid="new-chat-branch-input"
                />
                <p className="chat-list__new-hint">
                  The branch this chat's container commits to and opens a pull request for.
                </p>
              </div>
            )}
            <button
              className="chat-list__new-start"
              onClick={handleCreate}
              disabled={creating}
              data-testid="new-chat-start-btn"
            >
              {creating ? 'Starting...' : 'Start Chat'}
            </button>
          </div>,
          document.body,
        )}
      </div>
      <div className="chat-list__items">
        {loading && items.length === 0 && (
          <div className="chat-list__empty" role="status">Loading chats...</div>
        )}
        {!loading && items.length === 0 && (
          <div className="chat-list__empty">No chats yet. Start a conversation!</div>
        )}
        {activeChats.map((chat) => (
          <ChatPanel
            key={chat.id}
            chat={chat}
            sending={!!sending[chat.id]}
            sendError={sendErrors[chat.id] || null}
            interrupting={!!interrupting[chat.id]}
            stopping={!!stopping[chat.id]}
            authenticated={!!user}
            onSend={(content, images) => handleSend(chat.id, content, images)}
            onClearSendError={() => dispatch(clearSendError(chat.id))}
            onInterrupt={() => handleInterrupt(chat.id)}
            onStop={() => handleStop(chat.id)}
            onExpand={() => handleExpand(chat.id)}
          />
        ))}
        {(closedChats.length > 0 || closedTotal > 0) && (
          <div className="chat-list__closed-section" data-testid="closed-chats-section">
            <button
              className="chat-list__closed-header"
              onClick={() => setClosedExpanded(!closedExpanded)}
              aria-expanded={closedExpanded}
              data-testid="closed-chats-toggle"
            >
              {closedExpanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
              <span className="chat-list__closed-label">Closed Chats</span>
              <span className="chat-list__closed-count">{closedTotal}</span>
            </button>
            {closedExpanded && (
              <>
                {closedChats.map((chat) => (
                  <ChatPanel
                    key={chat.id}
                    chat={chat}
                    sending={!!sending[chat.id]}
                    sendError={sendErrors[chat.id] || null}
                    interrupting={!!interrupting[chat.id]}
                    stopping={!!stopping[chat.id]}
                    authenticated={!!user}
                    onSend={(content, images) => handleSend(chat.id, content, images)}
                    onClearSendError={() => dispatch(clearSendError(chat.id))}
                    onInterrupt={() => handleInterrupt(chat.id)}
                    onStop={() => handleStop(chat.id)}
                    onExpand={() => handleExpand(chat.id)}
                  />
                ))}
                {closedLoaded < closedTotal && (
                  <button
                    className="chat-list__show-more"
                    onClick={() => dispatch(fetchMoreClosedChats({ appId, offset: closedLoaded }))}
                    disabled={loadingMore}
                    data-testid="show-more-closed-chats"
                  >
                    {loadingMore ? 'Loading...' : `Show More (${closedTotal - closedLoaded} remaining)`}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default ChatList
